import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import TextField from "@/common/components/TextField";
import { zodResolver } from "@hookform/resolvers/zod";
import { QueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { createSchema, PermissionValidationSchema } from "@/features/permissions/schemas/create.schema";
import { FormTransformers } from "@/common/utils/formTransformers";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import SelectController from "@/common/components/SelectController";
import { useTmaisPermissionsOperationKlasses } from "@/common/hooks/queries/useTmaisPermissionsOperationKlasses";
import TextArea from "@/common/components/TextArea";
import useDebounce from "@/common/hooks/useDebounce";
import useCreateTmaisPermission from "../../hooks/useCreateTmaisPermission";
import { ShowHeader } from "@/common/components/ShowHeader";
import useUserPermissions from "@/common/hooks/useUserPermissions";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import Checkbox from "@/common/components/Checkbox";

const Create = () => {
  const navigate = useNavigate();
  const queryClient = new QueryClient()

  const [messages, setMessages] = useState<string[] | boolean>(false)
  const [showOnlyWithoutPermmission, setShowOnlyWithoutPermmission] = useState(true);

  const [searchPermissionOperationKlass, setSearchPermissionOperationKlass] = useDebounce('');

  const {
    mutate,
    isPending
  } = useCreateTmaisPermission({
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['tmaisPermissions']})
      navigate('/tmais-permissions')
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({ setError, setMessages })(error)
    }
  })

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()
  const userHasPermissionToCreateTmaisPermission =
    isUserPermissionsPending || hasUserPermission('Admin::Tmais::Permissions::Create')

  const { data: tmaisOperationKlassesOptions } = useTmaisPermissionsOperationKlasses({
    filters: { 
      search: searchPermissionOperationKlass,
      withoutPermission: showOnlyWithoutPermmission
    },
    mapper: (data: string[]) => data?.map((item) => ({ value: item, label: item }))
  });

  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors }
  } = useForm<PermissionValidationSchema>({
    resolver: zodResolver(createSchema)
  });

  const onSubmit: SubmitHandler<PermissionValidationSchema> = (data) => mutate(data);

  return (
    <form 
      onSubmit={handleSubmit(onSubmit)} 
      autoComplete="false"
      className="flex flex-col max-h-[calc(100vh-64px)]"
    >
      <ShowHeader
        title="Permissões - Tmais"
        subtitle="Nova permissão"
        goBack
        goBackProps={{
          fallback: '/tmais-permissions'
        }}
        buttons={(
          <div className="flex gap-4">
            <Link to='/tmais-permissions'>
              <Button variant='outline-error'>
                Cancelar
              </Button>
            </Link>

            <Button 
              data-testid='create_tmais_permission' 
              type="submit" 
              className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]" 
              isLoading={isPending}
              disabled={!userHasPermissionToCreateTmaisPermission}
            >
              Confirmar
            </Button>
          </div>
        )}
      />

      <ErrorMessageWrapper statusCode={userHasPermissionToCreateTmaisPermission ? 200 : 403}>
        <Box className="flex flex-col gap-4 flex-1 overflow-auto shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
          <div className="flex justify-between items-center">
            <p className="text-lg font-bold">
              Nova permissão
            </p>

            <Checkbox
              label="Mostrar classes de operação sem permissão vinculada"
              className="italic"
              checked={showOnlyWithoutPermmission}
              onChange={() => setShowOnlyWithoutPermmission(!showOnlyWithoutPermmission)}
            />
          </div>

          <div className="flex gap-2">
            <TextField
              label="Nome"
              markAsRequired
              errorMessage={errors?.display_name?.message}
              className="w-1/2"
              {...register('display_name')}
            />
            <SelectController
              name='operation_klass'
              control={control}
              label="Classe de operação"
              markAsRequired
              onInputChange={setSearchPermissionOperationKlass}
              options={tmaisOperationKlassesOptions}
              errorMessage={errors?.operation_klass?.message}
              className="w-1/2"
            />
          </div>

          <TextField
            label="Descrição"
            markAsRequired
            errorMessage={errors?.description?.message}
            {...register('description')}
          />

          <TextArea
            label="Propiedades"
            placeholder="{}"
            {...register('properties')}
            errorMessage={errors.properties?.message || undefined}
            rows={8}
          />
        </Box>

        {messages && (
          <ModalMessageSimple
            messages={messages}
            setMessages={setMessages}
            isError
          />
        )}
      </ErrorMessageWrapper>
    </form>
  );
};

export default Create;
